import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Typography, Box } from '@mui/material';
import { format } from 'date-fns';

import { addNotificationAction } from 'store/notifications/notificationsActions';
import { MessageModal } from 'ui/Modals';
import { shortDateFormat } from 'shared/constants/dateFormat';

import { sendMessageToSales } from '../../services';

const ShopProductCancellationModal = ({ open, onClose, onConfirm, product }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const toggleMessageModal = () => setMessageModalOpen(!messageModalOpen);

  const handleConfirm = () => {
    onClose();
    onConfirm();
  };

  const sendMessage = messageData => {
    sendMessageToSales(messageData)
      .then(() => {
        dispatch(
          addNotificationAction({
            message: t('Message sent successfully'),
            variant: 'success',
          }),
        );
      })
      .catch(() => {
        dispatch(
          addNotificationAction({
            message: t('Failed to send a message'),
            variant: 'error',
          }),
        );
      });
  };

  return (
    <div>
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>
          <Typography variant="h4">
            {t('Cancellation:')} {product.name}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t('We are sorry to hear that you have decided to cancel the following subscription:')}
          </Typography>

          <Box mt={4}>
            <Typography>
              {t('Subscription:')} <strong>{product.name}</strong>
            </Typography>
            <Typography>
              {t('Cancellation date:')} <strong>{format(new Date(), shortDateFormat)}</strong>
            </Typography>
            <Typography>
              {t('Subscription ends:')}{' '}
              <strong>{format(new Date(product.subscriptionUntil), shortDateFormat)}</strong>
            </Typography>
          </Box>
          <Box mt={4}>
            <Typography>
              {t(
                'If you are uncertain whether you really want to cancel the subscription, we would be delighted to assist you with any open questions. Please do not hesitate to contact your personal advisor or the Service Center here:',
              )}
            </Typography>
            <Box mt={4}>
              <Button variant="contained" color="primary" onClick={toggleMessageModal}>
                {t('Contact sales')}
              </Button>
            </Box>
            <MessageModal
              title={t('Contact Sales')}
              open={messageModalOpen}
              onClose={toggleMessageModal}
              onSendMessage={sendMessage}
            />
          </Box>
          <Box mt={4}>
            <Typography>
              {t(
                'If you want to move ahead and cancel the subscription, please confirm your selection below.',
              )}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary" variant="outlined">
            {t('Cancel')}
          </Button>
          <Button onClick={handleConfirm} color="primary" variant="contained" autoFocus>
            {t('Confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

ShopProductCancellationModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  product: PropTypes.object.isRequired,
};

export { ShopProductCancellationModal };
